import { useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { cn } from "@pontimed/ui";

/**
 * Top-of-viewport indeterminate progress bar shown during navigation.
 * Mirrors apps/web NavigationProgress so the SPA and marketing site feel
 * cohesive. Triggers on:
 *   - TanStack Router pending/loading state (internal SPA transitions)
 *   - any user-initiated `<a>` click that leaves the current URL
 *     (cross-origin nav e.g. → marketing site, or hard reloads)
 *
 * Click listener uses CAPTURE phase so it runs before TanStack Link's bubble-
 * phase handler.
 *
 * Show-delay model (nprogress-style): the bar is revealed ONLY if a navigation
 * is still in-flight after SHOW_DELAY_MS. Instant soft navs — the common case,
 * kept hot by `defaultPreload: "intent"` — show NOTHING, which is what makes
 * them feel instant. This replaces the old "force the bar visible ≥400ms" floor
 * that fabricated lag on every navigation, including the instant ones.
 */
const SHOW_DELAY_MS = 150;
// Once the bar IS shown, keep it up at least this long so a near-threshold nav
// doesn't flash for a single frame.
const MIN_VISIBLE_MS = 300;

export function NavigationProgress() {
  const isLoading = useRouterState({
    select: (s) => s.isLoading || s.isTransitioning,
  });

  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shownAtRef = useRef<number>(0);

  // start/stop close over refs only (stable) → safe to call from any effect.
  const startRef = useRef(() => {
    if (showTimer.current || pendingRef.current) return;
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    showTimer.current = setTimeout(() => {
      showTimer.current = null;
      pendingRef.current = true;
      shownAtRef.current = Date.now();
      setPending(true);
    }, SHOW_DELAY_MS);
  });

  const stopRef = useRef(() => {
    if (showTimer.current) {
      // Navigation finished before the show delay — never reveal the bar.
      clearTimeout(showTimer.current);
      showTimer.current = null;
      return;
    }
    if (!pendingRef.current) return;
    const elapsed = Date.now() - shownAtRef.current;
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
    hideTimer.current = setTimeout(() => {
      hideTimer.current = null;
      pendingRef.current = false;
      setPending(false);
    }, remaining);
  });

  useEffect(() => {
    if (isLoading) startRef.current();
    else stopRef.current();
  }, [isLoading]);

  // Cross-origin / hard-nav link clicks (leaving the SPA): the router's
  // isLoading won't fire, so arm the same show-delay manually.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.button !== 0) return;

      const target = e.target as HTMLElement | null;
      const link = target?.closest("a") ?? null;
      if (!link) return;
      if (link.target && link.target !== "_self") return;
      if (link.hasAttribute("download")) return;

      const href = link.getAttribute("href");
      if (!href) return;
      if (
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        href.startsWith("javascript:")
      ) {
        return;
      }

      let url: URL;
      try {
        url = new URL(link.href, window.location.href);
      } catch {
        return;
      }

      const sameOrigin = url.origin === window.location.origin;
      const samePath = url.pathname === window.location.pathname;
      const sameSearch = url.search === window.location.search;
      if (sameOrigin && samePath && sameSearch) return;

      startRef.current();
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      if (showTimer.current) {
        clearTimeout(showTimer.current);
        showTimer.current = null;
      }
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      pendingRef.current = false;
      setPending(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(
    () => () => {
      if (showTimer.current) clearTimeout(showTimer.current);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    },
    [],
  );

  return (
    <div
      className={cn(
        "pointer-events-none fixed inset-x-0 top-0 z-[100] h-1.5 overflow-hidden transition-opacity duration-150",
        pending ? "opacity-100" : "opacity-0",
      )}
      aria-hidden={!pending}
    >
      <div className="bg-primary/25 absolute inset-0" />
      <div className="via-primary absolute inset-0 h-full w-2/5 animate-[progress-indeterminate_1.4s_ease-in-out_infinite] bg-gradient-to-r from-transparent to-transparent" />
    </div>
  );
}
